import { useState, useCallback } from 'react';
import { startOfWeek, addDays } from 'date-fns';
import type { TimeBlock, CalendarProps, ModalType } from './types';
import { CalendarHeader } from './components/CalendarHeader';
import { TimeLabels } from './components/TimeLabels';
import { DayColumn } from './components/DayColumn';
import { ModalBackdrop } from './modals/ModalBackdrop';
import { EditModal } from './modals/EditModal';
import { DeleteModal } from './modals/DeleteModal';
import { useCalendar } from './hooks/useCalendar';

export const Calendar = ({
  timeBlocks,
  onTimeBlockCreate,
  onTimeBlockUpdate,
  onTimeBlockDelete,
}: CalendarProps) => {
  const calendar = useCalendar();
  const [currentWeek] = useState(startOfWeek(new Date()));
  const [editingBlock, setEditingBlock] = useState<TimeBlock | null>(null);
  const [modalType, setModalType] = useState<ModalType | null>(null);

  const handleBlockCreate = useCallback(
    (result: { startTime: Date; endTime: Date }) => {
      const startTime = new Date(result.startTime);
      const endTime = new Date(result.endTime);
      onTimeBlockCreate({
        id: crypto.randomUUID(),
        day: startTime,
        startTime,
        endTime,
        title: 'New Time Block',
      });
    },
    [onTimeBlockCreate],
  );

  const handleEditClick = useCallback(
    (e: React.MouseEvent, block: TimeBlock) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('editing block', block);
      setEditingBlock(block);
      setModalType('edit');
    },
    [],
  );

  const handleDeleteClick = useCallback(() => {
    setModalType('delete-confirm');
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalType(null);
    setEditingBlock(null);
  }, []);

  const handleTitleChange = useCallback(
    (block: TimeBlock, title: string) => {
      const updatedBlock = { ...block, title };
      setEditingBlock(updatedBlock);
      onTimeBlockUpdate(updatedBlock);
    },
    [onTimeBlockUpdate],
  );

  const handleTimeChange = useCallback(
    (field: 'startTime' | 'endTime', value: string) => {
      if (!editingBlock) return;
      const [hours, minutes] = value.split(':').map(Number);
      const newTime = new Date(editingBlock[field]);
      newTime.setHours(hours, minutes, 0, 0);
      const updatedBlock = { ...editingBlock, [field]: newTime };
      setEditingBlock(updatedBlock);
      onTimeBlockUpdate(updatedBlock);
    },
    [editingBlock, onTimeBlockUpdate],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!editingBlock) return;
    onTimeBlockDelete(editingBlock.id);
    handleCloseModal();
  }, [editingBlock, onTimeBlockDelete, handleCloseModal]);

  console.log(editingBlock);

  return (
    <div className="flex flex-col min-h-full">
      <CalendarHeader currentWeek={currentWeek} />
      <div className="flex">
        <TimeLabels />

        <div className="flex flex-1">
          {[1, 2, 3, 4, 5, 6, 0].map((offset) => {
            const day = addDays(currentWeek, offset);
            return (
              <DayColumn
                key={offset}
                day={day}
                timeBlocks={timeBlocks}
                focusedBlockId={calendar.state.focusedTimeBlockId}
                editingBlockId={editingBlock ? editingBlock.id : null}
                onBlockCreate={handleBlockCreate}
                onTitleChange={handleTitleChange}
                onTitleBlur={() => {}}
                onEditClick={handleEditClick}
                onDeleteClick={(block) => {
                  setEditingBlock(block);
                  setModalType('delete-confirm');
                }}
                onUnfocusBlock={calendar.actions.unfocusTimeBlock}
                onFocusBlock={calendar.actions.focusTimeBlock}
              />
            );
          })}
        </div>
      </div>

      {modalType === 'edit' && editingBlock && (
        <ModalBackdrop onClose={handleCloseModal}>
          <EditModal
            block={editingBlock}
            onClose={handleCloseModal}
            onTitleChange={handleTitleChange}
            onTimeChange={handleTimeChange}
            onDeleteClick={handleDeleteClick}
          />
        </ModalBackdrop>
      )}

      {modalType === 'delete-confirm' && (
        <ModalBackdrop onClose={handleCloseModal}>
          <DeleteModal
            onClose={handleCloseModal}
            onCancel={handleCloseModal}
            onConfirm={handleDeleteConfirm}
          />
        </ModalBackdrop>
      )}
    </div>
  );
};
